/**
 * The affix registry.
 *
 * One import and one entry per affix, and nothing else. Every number lives in the affix's own
 * file, so adding the seventeenth affix is a file and a line rather than an edit to anything that
 * rolls or applies one.
 *
 * `PREFIX_ORDER` and `SUFFIX_ORDER` are part of the determinism contract: a roll is an index into
 * one of them, so reordering either reshuffles every weapon every peer would have generated.
 */

import { ATOMIC_PRIMER } from '@/sim/items/affixes/prefixes/atomicPrimer';
import { DELUXE_BARREL } from '@/sim/items/affixes/prefixes/deluxeBarrel';
import { EXECUTIVE_TRIGGER } from '@/sim/items/affixes/prefixes/executiveTrigger';
import { MUNICIPAL_COMPENSATOR } from '@/sim/items/affixes/prefixes/municipalCompensator';
import { OVERWOUND_SPRING } from '@/sim/items/affixes/prefixes/overwoundSpring';
import { REGULATION_BRACE } from '@/sim/items/affixes/prefixes/regulationBrace';
import { SANITARY_GRIP } from '@/sim/items/affixes/prefixes/sanitaryGrip';
import { SURPLUS_MAGAZINE } from '@/sim/items/affixes/prefixes/surplusMagazine';
import { DEAD_WEIGHT } from '@/sim/items/affixes/suffixes/deadWeight';
import { LOOSE_FURNITURE } from '@/sim/items/affixes/suffixes/looseFurniture';
import { SALT_CORROSION } from '@/sim/items/affixes/suffixes/saltCorrosion';
import { SHORT_SHIFT } from '@/sim/items/affixes/suffixes/shortShift';
import { STICKY_SEAR } from '@/sim/items/affixes/suffixes/stickySear';
import { UNION_MANDATE } from '@/sim/items/affixes/suffixes/unionMandate';
import { WARPED_BORE } from '@/sim/items/affixes/suffixes/warpedBore';
import { WILD_RETURN } from '@/sim/items/affixes/suffixes/wildReturn';
import type { Affix, AffixId } from '@/sim/items/affixContract';

export const AFFIXES = {
  deluxeBarrel: DELUXE_BARREL,
  atomicPrimer: ATOMIC_PRIMER,
  executiveTrigger: EXECUTIVE_TRIGGER,
  overwoundSpring: OVERWOUND_SPRING,
  regulationBrace: REGULATION_BRACE,
  municipalCompensator: MUNICIPAL_COMPENSATOR,
  surplusMagazine: SURPLUS_MAGAZINE,
  sanitaryGrip: SANITARY_GRIP,
  warpedBore: WARPED_BORE,
  saltCorrosion: SALT_CORROSION,
  stickySear: STICKY_SEAR,
  unionMandate: UNION_MANDATE,
  looseFurniture: LOOSE_FURNITURE,
  wildReturn: WILD_RETURN,
  shortShift: SHORT_SHIFT,
  deadWeight: DEAD_WEIGHT,
} as const satisfies Record<AffixId, Affix>;

/** The upside pool, in the order a roll indexes into. */
export const PREFIX_ORDER: readonly AffixId[] = [
  'deluxeBarrel',
  'atomicPrimer',
  'executiveTrigger',
  'overwoundSpring',
  'regulationBrace',
  'municipalCompensator',
  'surplusMagazine',
  'sanitaryGrip',
];

/** The penalty pool, in the order a roll indexes into and walks on a field collision. */
export const SUFFIX_ORDER: readonly AffixId[] = [
  'warpedBore',
  'saltCorrosion',
  'stickySear',
  'unionMandate',
  'looseFurniture',
  'wildReturn',
  'shortShift',
  'deadWeight',
];

/** Affix lookup, total over the union so it never widens to `undefined`. */
export function affix(id: AffixId): Affix {
  return AFFIXES[id];
}

/** Whether `value` names an affix. The gate a save goes through, since it stores ids as strings. */
export function isAffixId(value: string): value is AffixId {
  return Object.hasOwn(AFFIXES, value);
}
